/**
 * 디자인 토큰 (테마)
 *
 * lightTheme을 기준으로 darkTheme은 색상만 오버라이드한다.
 * styled-components DefaultTheme은 lightTheme 타입으로 확장된다.
 */

// ─────────────────────────────────────────────────────────────
// 라이트 테마
// ─────────────────────────────────────────────────────────────

export const lightTheme = {
  colors: {
    // 브랜드 색상
    primary: '#76A0FF',

    // 폰트 색상
    fontBlack: '#000000',
    fontBlackSecondary: '#3C3C3C',
    fontGray: {
      50: '#4E4E4E',
      100: '#6F6F6F',
      200: '#626262',
      300: '#797979',
      350: '#808080',
      400: '#828282',
      500: '#848484',
      600: '#878787',
      700: '#898989',
      800: '#7D7D7D',
      900: '#5D5D5D',
    },
    labelGray: '#A2A2A2',
    fontBlackTab: '#1C1C1C',
    fontBlackText: '#1F1F1F',
    fontGraySecondary: '#959595',
    fontGrayLight: '#C9C9C9',
    fontWhite: '#FFFFFF',

    // 배경 색상
    bgWhite: '#FFFFFF',
    bgGray: {
      100: '#FAFAFA',
      200: '#F0F0F0',
      250: '#F5F5F5',
      300: '#F6F6F6',
      400: '#F8F9FA',
      500: '#F7F7F7',
      600: '#D9D9D9',
      700: '#E8E8E8',
      800: '#BEBEBE',
      900: '#F2F2F2',
    },
    bgLightBlue: '#E8EFFF',
    btnBgGray: '#EFEFEF',

    // 테두리
    borderGray: '#D4D4D4',
    borderGraySecondary: '#E9E9E9',
    borderGrayLight: '#ECECEC',
    borderGrayLightSecondary: '#D6D6D6',

    // 상태 색상
    fontRedError: '#FF5D5D',
    btnDisabled: '#B7B7B7',
    btnRed: '#F23D3D',
    reportRed: '#FF7676',
    cartCountRed: '#FF5B5B',
    errorCodeRed: '#FF0000',
    successGreen: '#449f47ff',
    verifiedGreen: '#65C300',
    errorRed: '#FF5252',

    // 플레이어 색상
    borderGrayPlayer: '#E6E6E6',
    playBtnBgGray: '#2E2E2E',
    playBtnBgHover: '#1A1A1A',
    playBtnGray: '#6A6A6A',

    // 시맨틱 색상
    text: '#1F1F1F',
    textSecondary: '#959595',
    background: '#FFFFFF',
    backgroundSecondary: '#FAFAFA',
    backgroundTertiary: '#F0F0F0',
    hover: '#F0F0F0',
    active: '#E8E8E8',
    primaryHover: '#5A80FF',
    primaryActive: '#4A70FF',
    error: '#FF5252',
    errorLight: '#FFE8E8',
    errorDark: '#CC0000',
  },

  gradients: {
    logo: 'linear-gradient(135deg, #7BD7FB 0%, #99A7FF 100%)',
    footer: 'linear-gradient(135deg, #72B7EC 0%, #A299F4 100%)',
    button: 'linear-gradient(135deg, #6B8EFF 0%, #9FE2FF 100%)',
    buttonHover: 'linear-gradient(135deg, #5A7EFF 0%, #8FD7FF 100%)',
  },

  shadows: {
    card: '0 2px 8px rgba(0, 0, 0, 0.04)',
    cardHover: '0 4px 16px rgba(0, 0, 0, 0.08)',
    player: '0 -2px 16px rgba(0, 0, 0, 0.1)',
  },

  fonts: {
    pretendard: 'Pretendard Variable, Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Open Sans", "Helvetica Neue", sans-serif',
  },

  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },

  zIndex: {
    modal: 1000,
    dropdown: 100,
    header: 50,
    player: 40,
  },

  playerHeight: 105,
  playerHeightMobile: 80,
};

// ─────────────────────────────────────────────────────────────
// 다크 테마 (색상만 오버라이드)
// ─────────────────────────────────────────────────────────────

export const darkTheme = {
  ...lightTheme,
  colors: {
    primary: '#76A0FF',

    // 폰트 색상
    fontBlack: '#FFFFFF',
    fontBlackSecondary: '#E0E0E0',
    fontGray: {
      50: '#B0B0B0',
      100: '#B0B0B0',
      200: '#A8A8A8',
      300: '#989898',
      350: '#909090',
      400: '#888888',
      500: '#787878',
      600: '#686868',
      700: '#585858',
      800: '#909090',
      900: '#A0A0A0',
    },
    labelGray: '#A2A2A2',
    fontBlackTab: '#F0F0F0',
    fontBlackText: '#FFFFFF',
    fontGraySecondary: '#A0A0A0',
    fontGrayLight: '#707070',
    fontWhite: '#000000',

    // 배경 색상
    bgWhite: '#1A1A1A',
    bgGray: {
      100: '#242424',
      200: '#2A2A2A',
      250: '#272727',
      300: '#2E2E2E',
      400: '#323232',
      500: '#262626',
      600: '#3A3A3A',
      700: '#3E3E3E',
      800: '#303030',
      900: '#2F2F2F',
    },
    bgLightBlue: '#1E2A3A',
    btnBgGray: '#2A2A2A',

    // 테두리
    borderGray: '#404040',
    borderGraySecondary: '#363636',
    borderGrayLight: '#5A5A5A',
    borderGrayLightSecondary: '#3E3E3E',

    // 상태 색상
    fontRedError: '#FF6B6B',
    btnDisabled: '#5A5A5A',
    btnRed: '#F23D3D',
    reportRed: '#FF7676',
    cartCountRed: '#FF5B5B',
    errorCodeRed: '#FF0000',
    successGreen: '#449f47ff',
    verifiedGreen: '#65C300',
    errorRed: '#FF5252',

    // 플레이어 색상
    borderGrayPlayer: '#2C2C2C',
    playBtnBgGray: '#4A4A4A',
    playBtnBgHover: '#5A5A5A',
    playBtnGray: '#C0C0C0',

    // 시맨틱 색상 (GPL 디자인 시스템)
    text: '#FFFFFF',
    textSecondary: '#A0A0A0',
    background: '#1A1A1A',
    backgroundSecondary: '#242424',
    backgroundTertiary: '#2A2A2A',
    hover: '#2A2A2A',
    active: '#3E3E3E',
    primaryHover: '#5A80FF',
    primaryActive: '#4A70FF',
    error: '#FF5252',
    errorLight: '#2A1515',
    errorDark: '#CC0000',
  },
};

// ─────────────────────────────────────────────────────────────
// 타입 정의
// ─────────────────────────────────────────────────────────────

export type Theme = typeof lightTheme;

/** styled-components DefaultTheme 확장 */
declare module 'styled-components' {
  export interface DefaultTheme extends Theme {}
}
